import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaEllipsisV } from 'react-icons/fa';
import Sheet from '../common/Sheet';

/**
 * ActionsMenu — a reusable trigger + list of actions. Used several ways in MCPlayback (2c):
 *   - per-cell `•••` (default ⋮ trigger, `header` = cam name): Snapshot · Download · Archive (set/unset)
 *     · Export (set/unset) · Bookmark (v2).
 *   - bulk-actions menu in the transport (conf>1): `header` = "Apply to all N visible cams".
 *   - saved-layout menus in the desktop sidebar / camera drawer.
 *
 * Two presentations, chosen by viewport:
 *   - MOBILE-PORTRAIT (base): a **bottom SHEET** — same `.mcpb-sheet` pattern as every other mobile menu
 *     (transport Layout/Speed/Cog, ribbon Views/Filters/Go-to-time). A positioned popover can't fit a phone
 *     (a left-column cell's `align:right` menu ran off the left edge), so the sheet is the correct base.
 *   - LARGER SCREENS (accommodation): the original popover, PORTALED into `.mcplayback-container` and
 *     position:fixed at the trigger so a playing `<video>`'s GPU layer / the grid stacking can't paint
 *     through it (David 2026-06-19: "nothing should bleed through"). `openUp`/`align` place it there.
 *
 * Each item: { action, label, title, path, onClick?, active?, disabled?, placeholder? }. A disabled item
 * never fires; `active` marks Archive/Export when the clip carries that flag; `placeholder` shows a "v2"
 * badge (Bookmark). Outside-click / scrim / select closes.
 */

// Bottom-SHEET vs positioned-popover gate: a bottom sheet BELOW desktop (< 1280), popover at >= 1280 — the
// same touch->mouse line the Live bottom-bar selectors use (util/tabletSheet). Covers mobile portrait, phone
// landscape, AND tablet (both orientations), so the Bulk menu + per-cell ••• match Views/Filters/When, which
// are bottom sheets everywhere below desktop (David #86bb7phhh S25 fixed mobile-landscape; S35 extends it to
// tablet, which was still falling back to the off-screen popover). Menu-only gate — the LAYOUT has its own
// isMobilePortrait in MCPlaybackView — so it's defined here.
const MOBILE_SHEET_MQ = '(max-width: 1279px)';

// Sheet-mode flag (matchMedia). Absent matchMedia (jsdom/unit tests) → false → popover mode, so the
// existing popover tests exercise the popover unchanged.
const useMobileSheet = () => {
  const [match, setMatch] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(MOBILE_SHEET_MQ).matches : false),
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_SHEET_MQ);
    const onChange = () => setMatch(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);
  return match;
};

const ActionsMenu = ({
  items = [],
  triggerIcon,
  triggerLabel,
  triggerClassName = '',
  triggerAriaLabel = 'Actions',
  header,
  footer = null,
  align = 'right',
  openUp = false,
  openSignal = 0,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const asSheet = useMobileSheet();

  // Controlled open: the parent bumps `openSignal` (e.g. a cell right-click) to open this menu the SAME
  // as clicking the trigger — parity with Live's tile context-menu (David 2026-08-06). Starts at 0 (closed);
  // each bump to a new truthy value opens it.
  useEffect(() => { if (openSignal) setOpen(true); }, [openSignal]);

  // Position the portaled popover at the trigger (recomputed each open). Skipped in sheet mode (the sheet
  // is bottom-anchored, no trigger math). Fixed coords relative to the viewport; the menu is transient so
  // we do not track scroll/resize.
  useLayoutEffect(() => {
    if (!open || asSheet || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const gap = 4;
    // Reset all four edges (the base CSS sets top: calc(100%+4px); leaving it would conflict with an
    // openUp `bottom` and collapse the menu height). Then set only the two edges we want.
    const p = { position: 'fixed', top: 'auto', bottom: 'auto', left: 'auto', right: 'auto' };
    if (openUp) p.bottom = Math.max(4, window.innerHeight - r.top + gap);
    else p.top = r.bottom + gap;
    if (align === 'left') p.left = Math.max(4, r.left);
    else p.right = Math.max(4, window.innerWidth - r.right);
    setPos(p);
  }, [open, openUp, align, asSheet]);

  // Close on a click/tap outside BOTH the trigger and the menu/sheet (the scrim also closes in sheet mode),
  // OR on Escape (keyboard parity — a menu the user opened should always be dismissible without a mouse).
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Portal INTO the .mcplayback-container (not document.body) so the scoped CSS + theme/cam CSS-vars still
  // apply — at the container's top level so position:fixed + z-index lift it above the cells/video. Falls
  // back to body when there is no container (standalone unit tests).
  const portalTarget = (triggerRef.current && triggerRef.current.closest('.mcplayback-container')) || (typeof document !== 'undefined' ? document.body : null);

  // The action buttons — identical markup in both presentations (styling/state classes are viewport-agnostic).
  const itemEls = items.map((a) => (
    <button
      key={a.action}
      type="button"
      className={`mcpb-actions__item${a.active ? ' is-active' : ''}${a.placeholder ? ' is-placeholder' : ''}`}
      data-action={a.action}
      data-active={a.active ? 'true' : undefined}
      title={a.title}
      role="menuitem"
      disabled={!!a.disabled}
      aria-disabled={a.disabled ? 'true' : undefined}
      onClick={() => { if (a.disabled) return; if (a.onClick) a.onClick(a.action); setOpen(false); }}
    >
      <svg viewBox="0 0 24 24"><path d={a.path} /></svg>
      <span className="mcpb-actions__item-label">{a.label}</span>
      {a.placeholder && <span className="mcpb-actions__item-badge">v2</span>}
    </button>
  ));

  let overlay = null;
  if (open && portalTarget && asSheet) {
    // MOBILE: a bottom sheet via the shared <Sheet> (grip + corner ✕ + body — consistent with every
    // other closeable sheet). role="menu" + the header title are threaded through. menuRef sits on the
    // items list so the outside-click handler keeps item clicks "inside" (footer/chrome taps dismiss).
    overlay = createPortal(
      <Sheet
        className="mcpb-sheet mcpb-sheet--actions"
        scrimClassName="mcpb-sheet-scrim"
        role="menu"
        title={header || 'Actions'}
        ariaLabel={header || 'Actions'}
        onClose={() => setOpen(false)}
      >
        <div ref={menuRef} className="mcpb-actions__sheet-list">{itemEls}</div>
        {footer && <div className="mcpb-actions__footer">{footer}</div>}
      </Sheet>,
      portalTarget,
    );
  } else if (open && pos && portalTarget) {
    // LARGER SCREENS: the positioned popover.
    overlay = createPortal(
      <div ref={menuRef} className="mcpb-actions__menu" style={pos} role="menu">
        {header && <div className="mcpb-actions__header">{header}</div>}
        {itemEls}
        {footer && <div className="mcpb-actions__footer">{footer}</div>}
      </div>,
      portalTarget,
    );
  }

  return (
    <div className="mcpb-actions">
      <button
        ref={triggerRef}
        className={`mcpb-actions__trigger ${triggerClassName}`}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerAriaLabel}
        title={triggerAriaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        {triggerLabel && <span className="mcpb-actions__trigger-label">{triggerLabel}</span>}
        <span className="mcpb-actions__trigger-icon" aria-hidden="true">{triggerIcon || <FaEllipsisV />}</span>
      </button>
      {overlay}
    </div>
  );
};

export default ActionsMenu;
